import React, { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import DraftRestoreBanner from "./DraftRestoreBanner";
import { useAuth } from "@/lib/AuthContext";
import { Bell, Search, Sparkles, LogOut, User as UserIcon } from "lucide-react";

const TITLES = {
  "/": "Hallintapaneeli",
  "/invoicing": "Laskutus",
  "/accounting": "Kirjanpito",
  "/crm": "CRM · Asiakkuudet",
  "/hr": "HR & Palkat",
  "/inventory": "Varasto",
  "/restaurant": "Ravintola",
  "/auto-repair": "Autokorjaamo",
  "/fleet": "Kalusto",
  "/logistics": "Logistiikka",
  "/taxi": "Taksi",
  "/car-rental": "Autovuokraus",
  "/money-transfer": "Rahansiirto",
  "/receipts": "E-Kuitit",
  "/projects": "Projektit",
  "/quotes": "Tarjoukset",
  "/settings": "Asetukset",
  "/admin/users": "Käyttäjähallinta",
  "/admin/licenses": "Lisenssit",
  "/partner-hub": "Partner & Freelance Hub",
  "/bi": "AI Business Intelligence",
  "/customer-portal": "Asiakasportaali",
  "/upsell-portal": "Upsell-portaali",
  "/finance-reports": "Talousraportti",
  "/staff": "Henkilökunta",
  "/security": "Tietoturva & Compliance",
};

function findTitle(pathname) {
  if (TITLES[pathname]) return TITLES[pathname];
  const match = Object.keys(TITLES)
    .filter((k) => k !== "/" && pathname.startsWith(k))
    .sort((a, b) => b.length - a.length)[0];
  return match ? TITLES[match] : "Apps2Go Hub";
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const title = findTitle(location.pathname);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="app-bg flex min-h-screen">
      <Sidebar user={user} />

      <main className="flex-1 min-w-0 overflow-x-hidden relative z-10">
        {/* Topbar */}
        <div className="sticky top-0 z-30 px-4 lg:px-8 py-3 backdrop-blur-xl bg-[#0a0b2e]/60 border-b border-white/5">
          <div className="max-w-[1600px] mx-auto flex items-center gap-4">
            <div className="flex-1 min-w-0 lg:pl-0 pl-12">
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">
                Apps2Go Hub · {new Date().toLocaleDateString("fi-FI", { weekday: "long", day: "numeric", month: "long" })}
              </p>
              <h2 className="text-lg lg:text-xl font-display font-semibold text-white tracking-tight truncate">
                {title}
              </h2>
            </div>

            {/* Search */}
            <div className="topbar-search hidden md:flex items-center gap-2 px-4 py-2 w-64 lg:w-80">
              <Search className="w-4 h-4 text-white/40 flex-shrink-0" />
              <input
                type="text"
                placeholder="Hae sovelluksesta..."
                data-testid="topbar-search"
                className="bg-transparent text-sm text-white/90 placeholder-white/30 outline-none flex-1 min-w-0"
              />
              <kbd className="hidden lg:inline-block text-[10px] text-white/30 border border-white/10 rounded px-1.5 py-0.5 font-mono">⌘K</kbd>
            </div>

            {/* Notifications */}
            <button
              data-testid="topbar-notifications"
              className="relative w-10 h-10 rounded-full glass-soft flex items-center justify-center text-white/70 hover:text-white hover:border-amber-400/30 transition"
              title="Ilmoitukset"
            >
              <Bell className="w-4 h-4" />
              <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-amber-400 ring-2 ring-[#0a0b2e]" />
            </button>

            {/* User avatar dropdown */}
            <div className="relative">
              <button
                onClick={() => setMenuOpen((v) => !v)}
                data-testid="topbar-user-menu"
                className="flex items-center gap-2 pl-1 pr-2.5 py-1 rounded-full glass-soft hover:border-amber-400/30 transition"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                  {(user?.full_name || user?.email || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="hidden md:block text-left pr-1">
                  <p className="text-[11px] text-white/90 font-medium truncate max-w-[140px]">
                    {user?.full_name}
                  </p>
                  <p className="text-[9px] text-white/40 uppercase tracking-widest">
                    {user?.role === "admin" ? "Pääkäyttäjä" : user?.role === "license_customer" ? "Lisenssi" : "Käyttäjä"}
                  </p>
                </div>
              </button>

              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 top-12 z-40 w-56 glass-strong rounded-xl p-2 shadow-2xl">
                    <div className="px-3 py-2 border-b border-white/5 mb-1">
                      <p className="text-sm text-white/90 truncate">{user?.full_name}</p>
                      <p className="text-[11px] text-white/40 truncate">{user?.email}</p>
                    </div>
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        navigate("/settings");
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-white/5"
                    >
                      <UserIcon className="w-4 h-4" /> Profiili
                    </button>
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        navigate("/settings");
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-white/5"
                    >
                      <Sparkles className="w-4 h-4" /> Asetukset
                    </button>
                    <button
                      onClick={handleLogout}
                      data-testid="topbar-logout"
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-300 hover:bg-red-500/10 mt-1 border-t border-white/5 pt-2"
                    >
                      <LogOut className="w-4 h-4" /> Kirjaudu ulos
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 lg:p-8 max-w-[1600px] mx-auto animate-fade-in">
          <DraftRestoreBanner />
          <Outlet />
        </div>
      </main>
    </div>
  );
}
